import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/analytics";

const LanguageToggle = ({ scrolled }) => {
  const { lang, setLang } = useLanguage();
  const borderCls = scrolled ? "border-navy/20" : "border-white/40";
  const inactive = scrolled
    ? "text-navy/60 hover:text-brand"
    : "text-white/85 hover:text-brand drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]";
  return (
    <div
      className={`inline-flex items-center border ${borderCls} text-[11px] tracking-[0.2em] uppercase transition-colors`}
      data-testid="language-toggle"
    >
      <button
        type="button"
        onClick={() => setLang("en")}
        className={`px-3 py-1.5 transition-colors ${
          lang === "en" ? "bg-brand text-white" : inactive
        }`}
        data-testid="lang-en-btn"
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => setLang("ru")}
        className={`px-3 py-1.5 border-l ${borderCls} transition-colors ${
          lang === "ru" ? "bg-brand text-white" : inactive
        }`}
        data-testid="lang-ru-btn"
      >
        RU
      </button>
    </div>
  );
};

const Navbar = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navItems = [
    { to: "/", label: t.nav.home, key: "home" },
    { to: "/about", label: t.nav.about, key: "about" },
    { to: "/services", label: t.nav.services, key: "services" },
    { to: "/destinations", label: t.nav.destinations, key: "destinations" },
    { to: "/insights", label: t.nav.news, key: "insights" },
    { to: "/contact", label: t.nav.contact, key: "contact" },
  ];

  const headerCls = scrolled
    ? "bg-white/95 backdrop-blur-xl border-b border-navy/10 shadow-[0_4px_30px_rgba(11,30,53,0.08)] py-2"
    : "bg-gradient-to-b from-navy/70 via-navy/30 to-transparent py-5";

  const linkInactive = scrolled
    ? "text-navy/80 hover:text-brand"
    : "text-white hover:text-brand drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]";

  const mobileIconCls = scrolled
    ? "text-navy"
    : "text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerCls}`}
      data-testid="site-navbar"
    >
      <div className="px-6 sm:px-10 lg:px-16 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center group"
          data-testid="nav-logo"
        >
          <img
            src="/assets/brand/beyond-the-ordinary.png"
            alt="Rota Yacht Agency · Beyond the Ordinary"
            className={`w-auto transition-all duration-500 group-hover:-translate-y-0.5 ${
              scrolled ? "h-14 sm:h-16" : "h-16 sm:h-20"
            }`}
            style={{
              filter: scrolled ? "none" : "brightness(0) invert(1)",
            }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop">
          {navItems.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `text-[12px] tracking-[0.2em] uppercase transition-colors link-underline ${
                  isActive ? "text-brand" : linkInactive
                }`
              }
              data-testid={`nav-link-${item.key}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LanguageToggle scrolled={scrolled} />
          <Link
            to="/contact"
            onClick={() => trackEvent("request_support_click", { surface: "navbar_desktop" })}
            className="inline-block bg-brand text-white px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase font-semibold hover:bg-brand-deep transition-colors"
            data-testid="nav-cta-request"
          >
            {t.nav.requestSupport}
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden p-2 transition-colors ${mobileIconCls}`}
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          className={`lg:hidden mt-3 border-t ${
            scrolled
              ? "bg-white border-navy/10"
              : "bg-navy/95 backdrop-blur-xl border-white/10"
          }`}
        >
          <div className="px-6 py-6 space-y-4">
            {navItems.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `block text-sm tracking-[0.2em] uppercase ${
                    isActive
                      ? "text-brand"
                      : scrolled
                      ? "text-navy/80"
                      : "text-white/90"
                  }`
                }
                data-testid={`mobile-nav-link-${item.key}`}
              >
                {item.label}
              </NavLink>
            ))}
            <div
              className={`flex items-center justify-between pt-4 border-t ${
                scrolled ? "border-navy/10" : "border-white/10"
              }`}
            >
              <LanguageToggle scrolled={scrolled} />
              <Link
                to="/contact"
                onClick={() => trackEvent("request_support_click", { surface: "navbar_mobile" })}
                className="bg-brand text-white px-4 py-2 text-[11px] tracking-[0.2em] uppercase font-semibold"
                data-testid="mobile-nav-cta"
              >
                {t.nav.requestSupport}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
